import type { FC, FormEvent } from 'react';
import { useCallback, useState } from 'react';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Link,
  OutlinedInput,
  Select,
  MenuItem,
  Stack,
  SvgIcon,
  Typography,
  Grid
} from '@mui/material';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { paths } from 'src/paths';
import type { Page as PageType } from 'src/types/page';
import { supportMessagesApi } from 'src/api/supabase';

const Page: PageType = () => {
  usePageView();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      setIsSubmitting(true);

      const form = event.currentTarget;
      const formData = new FormData(form);

      // Extract form values
      const name = formData.get('name') as string;
      const email = formData.get('email') as string;
      const phone = formData.get('phone') as string;
      const supportTopic = formData.get('supportTopic') as 'app-help' | 'technical' | 'warranty' | 'scheduling' | 'billing' | 'general';
      const message = formData.get('message') as string;

      try {
        // Save to Supabase
        // console.log('Attempting to save support message to Supabase...');
        const supabaseResult = await supportMessagesApi.create({
          name,
          email,
          phone: phone || undefined,
          supportTopic,
          message,
        });

        if (supabaseResult) {
          // console.log('Support message saved to Supabase successfully:', supabaseResult.id);
        } else {
          // console.warn('Supabase returned null - message may not have been saved');
        }

        // Also send to Formspree as backup
        await fetch('https://formspree.io/f/xldppbno', {
          method: 'POST',
          body: formData,
          headers: {
            Accept: 'application/json',
          },
        });

        setSubmitted(true);
      } catch (error) {
        // console.error('Error submitting support form to Supabase:', error);
        // Still try Formspree if Supabase fails
        try {
          await fetch('https://formspree.io/f/xldppbno', {
            method: 'POST',
            body: formData,
            headers: {
              Accept: 'application/json',
            },
          });
        } catch (formspreeError) {
          // console.error('Formspree also failed:', formspreeError);
        }
        setSubmitted(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  const supportOptions = [
    {
      icon: <EmailIcon />,
      title: 'Email',
      description: 'We reply within 24 hours',
      detail: 'sales@mysticmediafilm.com'
    },
    {
      icon: <PhoneIcon />,
      title: 'Project Inquiries',
      description: 'Tell us about your project',
      detail: 'Use the form below'
    },
    {
      icon: <AccessTimeIcon />,
      title: 'Agency Hours',
      description: 'Monday – Friday',
      detail: '9:00 AM – 6:00 PM CT'
    }
  ];

  return (
    <>
      <Seo
        title="Support | Mystic Media Film"
        description="Need help with a Mystic Media Film project, invoice, or service question? Reach our Houston-based team for fast, friendly support on every account."
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          background: '#ffffff',
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Soft blue glows */}
        <Box
          sx={{
            position: 'absolute',
            top: '5%',
            right: '-10%',
            width: { xs: 400, md: 700 },
            height: { xs: 400, md: 700 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.1) 0%, transparent 70%)',
            filter: 'blur(100px)',
            pointerEvents: 'none'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '-15%',
            width: { xs: 400, md: 600 },
            height: { xs: 400, md: 600 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)',
            filter: 'blur(80px)',
            pointerEvents: 'none'
          }}
        />

        {/* Hero Section */}
        <Box
          sx={{
            pt: { xs: 10, md: 14 },
            pb: { xs: 6, md: 8 },
            position: 'relative',
            zIndex: 1
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={4}
alignItems="center"
textAlign="center">
              {/* Back link */}
              <Box sx={{ alignSelf: 'flex-start', mb: 2 }}>
                <Link
                  component={RouterLink}
                  href={paths.index}
                  sx={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    color: '#52525b',
                    '&:hover': { color: '#0a0a0b' }
                  }}
                  underline="hover"
                >
                  <SvgIcon sx={{ mr: 1, fontSize: 20 }}>
                    <ArrowLeftIcon />
                  </SvgIcon>
                  <Typography variant="body2">Back to Home</Typography>
                </Link>
              </Box>

              {/* Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(37, 99, 235, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SupportAgentIcon sx={{ fontSize: 40, color: '#2563eb' }} />
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  color: '#0a0a0b',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1
                }}
              >
                How Can We Help?
              </Typography>

              <Typography
                sx={{
                  color: '#52525b',
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  maxWidth: 600,
                  lineHeight: 1.7
                }}
              >
                Have a project idea, a question about a current build, or just need a quick hand?
                Drop us a note and we&apos;ll get back to you fast.
              </Typography>
            </Stack>
          </Container>
        </Box>

        {/* Support Options */}
        <Box sx={{ py: { xs: 4, md: 6 }, position: 'relative', zIndex: 1 }}>
          <Container maxWidth="lg">
            <Grid container
spacing={3}
justifyContent="center">
              {supportOptions.map((option, index) => (
                <Grid item
xs={12}
sm={4}
key={index}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backgroundColor: '#ffffff',
                      border: '1px solid #e4e4e7',
                      textAlign: 'center',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        backgroundColor: 'rgba(37, 99, 235, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2
                      }}
                    >
                      <SvgIcon sx={{ color: '#2563eb', fontSize: 28 }}>
                        {option.icon}
                      </SvgIcon>
                    </Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#0a0a0b', mb: 0.5 }}>
                      {option.title}
                    </Typography>
                    <Typography sx={{ color: '#52525b', fontSize: '0.9rem', mb: 1 }}>
                      {option.description}
                    </Typography>
                    <Typography sx={{ color: '#2563eb', fontWeight: 600, fontSize: '1rem' }}>
                      {option.detail}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Support Form Section */}
        <Box sx={{ py: { xs: 6, md: 10 }, position: 'relative', zIndex: 1 }}>
          <Container maxWidth="md">
            <Box
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                backgroundColor: '#ffffff',
                border: '1px solid #e4e4e7',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}
            >
              {submitted ? (
                <Stack spacing={3}
alignItems="center"
sx={{ py: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(16, 185, 129, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 48, color: '#10B981' }} />
                  </Box>
                  <Typography variant="h4"
sx={{ color: '#0a0a0b', fontWeight: 700, textAlign: 'center' }}>
                    Message Received!
                  </Typography>
                  <Typography sx={{ color: '#52525b', textAlign: 'center', maxWidth: 400 }}>
                    Thank you for contacting us. Our support team will get back to you within 24 hours.
                  </Typography>
                  <Button
                    component={RouterLink}
                    href={paths.index}
                    variant="outlined"
                    sx={{
                      mt: 2,
                      borderColor: '#e4e4e7',
                      color: '#0a0a0b',
                      '&:hover': {
                        borderColor: '#0a0a0b',
                        backgroundColor: '#fafafa'
                      }
                    }}
                  >
                    Back to Home
                  </Button>
                </Stack>
              ) : (
                <>
                  <Stack spacing={1}
sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography variant="h4"
sx={{ fontWeight: 700, color: '#0a0a0b' }}>
                      Send Us a Message
                    </Typography>
                    <Typography sx={{ color: '#52525b' }}>
                      Fill out the form below and we'll get back to you as soon as possible.
                    </Typography>
                  </Stack>

                  <form onSubmit={handleSubmit}>
                    <Grid container
spacing={3}>
                      <Grid item
xs={12}
sm={6}>
                        <FormControl fullWidth>
                          <FormLabel sx={{ color: '#0a0a0b', mb: 1, fontWeight: 500 }}>
                            Full Name *
                          </FormLabel>
                          <OutlinedInput
                            name="name"
                            required
                            placeholder="John Doe"
                            sx={{
                              backgroundColor: '#fafafa',
                              '& .MuiOutlinedInput-input': { color: '#0a0a0b' },
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e4e7' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a1a1aa' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563eb' }
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item
xs={12}
sm={6}>
                        <FormControl fullWidth>
                          <FormLabel sx={{ color: '#0a0a0b', mb: 1, fontWeight: 500 }}>
                            Email Address *
                          </FormLabel>
                          <OutlinedInput
                            name="email"
                            type="email"
                            required
                            placeholder="john@example.com"
                            sx={{
                              backgroundColor: '#fafafa',
                              '& .MuiOutlinedInput-input': { color: '#0a0a0b' },
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e4e7' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a1a1aa' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563eb' }
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item
xs={12}
sm={6}>
                        <FormControl fullWidth>
                          <FormLabel sx={{ color: '#0a0a0b', mb: 1, fontWeight: 500 }}>
                            Phone Number
                          </FormLabel>
                          <OutlinedInput
                            name="phone"
                            type="tel"
                            placeholder="(555) 123-4567"
                            sx={{
                              backgroundColor: '#fafafa',
                              '& .MuiOutlinedInput-input': { color: '#0a0a0b' },
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e4e7' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a1a1aa' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563eb' }
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item
xs={12}
sm={6}>
                        <FormControl fullWidth>
                          <FormLabel sx={{ color: '#0a0a0b', mb: 1, fontWeight: 500 }}>
                            Support Topic *
                          </FormLabel>
                          <Select
                            name="supportTopic"
                            required
                            defaultValue=""
                            displayEmpty
                            sx={{
                              backgroundColor: '#fafafa',
                              color: '#0a0a0b',
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e4e7' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a1a1aa' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563eb' },
                              '& .MuiSelect-icon': { color: '#52525b' }
                            }}
                          >
                            <MenuItem value=""
disabled>Select a topic</MenuItem>
                            <MenuItem value="app-help">App / Controller Help</MenuItem>
                            <MenuItem value="technical">Technical Issue</MenuItem>
                            <MenuItem value="warranty">Warranty Claim</MenuItem>
                            <MenuItem value="scheduling">Scheduling / Appointments</MenuItem>
                            <MenuItem value="billing">Billing Question</MenuItem>
                            <MenuItem value="general">General Inquiry</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item
xs={12}>
                        <FormControl fullWidth>
                          <FormLabel sx={{ color: '#0a0a0b', mb: 1, fontWeight: 500 }}>
                            How can we help? *
                          </FormLabel>
                          <OutlinedInput
                            name="message"
                            required
                            placeholder="Please describe your question or issue in detail..."
                            multiline
                            minRows={5}
                            sx={{
                              backgroundColor: '#fafafa',
                              '& .MuiOutlinedInput-input': { color: '#0a0a0b' },
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e4e7' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a1a1aa' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563eb' }
                            }}
                          />
                        </FormControl>
                      </Grid>
                      {/* Hidden field to identify this as a support request */}
                      <input type="hidden"
name="formType"
value="Support Request" />
                    </Grid>
                    <Box sx={{ mt: 4 }}>
                      <Button
                        fullWidth
                        size="large"
                        variant="contained"
                        type="submit"
                        disabled={isSubmitting}
                        sx={{
                          py: 1.5,
                          fontWeight: 600,
                          fontSize: '1rem',
                          textTransform: 'none',
                          background: '#0a0a0b',
                          color: '#ffffff',
                          boxShadow: 'none',
                          '&:hover': { background: '#27272a', boxShadow: 'none' },
                          '&:disabled': {
                            background: 'rgba(10, 10, 11, 0.4)',
                            color: 'rgba(255, 255, 255, 0.7)'
                          }
                        }}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 2, textAlign: 'center', color: '#52525b' }}
                    >
                      * Required fields. We typically respond within 24 hours.
                    </Typography>
                  </form>
                </>
              )}
            </Box>
          </Container>
        </Box>

        {/* FAQ Link Section */}
        <Box sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1 }}>
          <Container maxWidth="md">
            <Box
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.06) 0%, rgba(99, 102, 241, 0.06) 100%)',
                border: '1px solid rgba(37, 99, 235, 0.2)',
                textAlign: 'center'
              }}
            >
              <Typography variant="h5"
sx={{ fontWeight: 700, color: '#0a0a0b', mb: 1 }}>
                Looking for quick answers?
              </Typography>
              <Typography sx={{ color: '#52525b', mb: 3 }}>
                Check out our frequently asked questions for instant help.
              </Typography>
              <Button
                component={RouterLink}
                href={`${paths.index}#faqs`}
                variant="outlined"
                sx={{
                  borderColor: '#2563eb',
                  color: '#2563eb',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#1e40af',
                    backgroundColor: 'rgba(37, 99, 235, 0.06)'
                  }
                }}
              >
                View FAQs
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Page;
